// Generates a deterministic set of circular water droplets at various positions and sizes
// Mimics the hydrophobic glass effect on the J7 logo.

const SEED_DROPS = (() => {
  // Pseudo-random but stable list (so SSR/hydration matches)
  const rng = (i) => {
    const x = Math.sin(i * 9301 + 49297) * 233280;
    return x - Math.floor(x);
  };
  const drops = [];
  for (let i = 0; i < 80; i++) {
    const r = 4 + rng(i) * 38; // 4 → 42 px radius
    drops.push({
      cx: rng(i + 100) * 100, // 0–100% horizontal
      cy: rng(i + 200) * 100, // 0–100% vertical
      r,
      blur: r > 25 ? 0.3 : 0,
      tail: r > 18 && rng(i + 300) > 0.55, // some have a streak underneath
      tailH: r * (1.5 + rng(i + 400) * 2.2),
    });
  }
  return drops;
})();

export default function HeroDropletsBg() {
  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Deep emerald glass gradient — matches J7 logo */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(120% 80% at 25% 15%, #1AA964 0%, #0B7A3E 35%, #064D27 80%, #03311A 100%)",
        }}
      />

      {/* Subtle highlight band — light catching the glass */}
      <div
        className="absolute inset-0 mix-blend-screen opacity-40"
        style={{
          background:
            "linear-gradient(115deg, transparent 30%, rgba(180,255,205,0.35) 48%, transparent 62%)",
        }}
      />

      {/* SVG of perfect-circle hydrophobic water droplets */}
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid slice"
        className="absolute inset-0 w-full h-full"
        aria-hidden="true"
      >
        <defs>
          {/* Droplet body — translucent with highlight */}
          <radialGradient id="drop-body" cx="35%" cy="32%" r="65%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.95)" />
            <stop offset="20%" stopColor="rgba(220,255,235,0.55)" />
            <stop offset="60%" stopColor="rgba(60,140,90,0.30)" />
            <stop offset="100%" stopColor="rgba(0,40,20,0.55)" />
          </radialGradient>
          {/* Tiny specular highlight */}
          <radialGradient id="drop-spec" cx="35%" cy="28%" r="20%">
            <stop offset="0%" stopColor="rgba(255,255,255,1)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
          </radialGradient>
          {/* Streak gradient for drops that have run a little */}
          <linearGradient id="drop-tail" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(255,255,255,0.45)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
          </linearGradient>
        </defs>

        {SEED_DROPS.map((d, i) => {
          // Convert radius from px (assumed on a 1000px wide canvas) to viewBox units
          const r = d.r / 10; // viewBox is 100 wide
          return (
            <g key={i}>
              {/* Optional dribble tail */}
              {d.tail && (
                <rect
                  x={d.cx - r * 0.35}
                  y={d.cy}
                  width={r * 0.7}
                  height={d.tailH / 10}
                  fill="url(#drop-tail)"
                  rx={r * 0.35}
                />
              )}
              {/* Soft outer shadow under droplet */}
              <ellipse
                cx={d.cx}
                cy={d.cy + r * 0.45}
                rx={r * 0.9}
                ry={r * 0.25}
                fill="rgba(0,0,0,0.25)"
                filter="blur(0.3px)"
              />
              {/* The droplet itself */}
              <circle
                cx={d.cx}
                cy={d.cy}
                r={r}
                fill="url(#drop-body)"
                stroke="rgba(255,255,255,0.35)"
                strokeWidth={0.08}
              />
              {/* Specular highlight */}
              <circle
                cx={d.cx - r * 0.32}
                cy={d.cy - r * 0.4}
                r={r * 0.32}
                fill="url(#drop-spec)"
              />
            </g>
          );
        })}
      </svg>

      {/* Subtle vignette so content reads clearly */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(100% 60% at 30% 60%, rgba(0,0,0,0) 0%, rgba(0,30,15,0.35) 100%)",
        }}
      />

      {/* Bottom fade into the rest of the white site */}
      <div className="absolute bottom-0 inset-x-0 h-40 bg-gradient-to-t from-white via-white/60 to-transparent" />
    </div>
  );
}
